import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell as MuiTableCell,
    Button
} from '@mui/material';
import { styled, tableCellClasses } from '@mui/material';
import { PrimaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';
import BasicMenu from '../components/BasicMenu';
import { departamentoService } from '../services/DepartamentoService';
import { Departamento } from '../types/negocio/Departamento';
import toastService from '../utils/toastService';
import DepartamentoModal from '../components/modais/DepartamentoModal';
import LoadingIndicator from '../components/LoadingIndicator';

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
        fontWeight: 'bold'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const Departamentos: React.FC = () => {
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [departamentoSelecionado, setDepartamentoSelecionado] = useState<Departamento>({} as Departamento);

    const criarDepartamentoVazio = (): Departamento => ({
        nome: '',
        descricao: ''
    });

    useEffect(() => {
        fetchDepartamentos();
    }, []);

    const fetchDepartamentos = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await departamentoService.listarTodos();
            
            if (Array.isArray(data)) {
                setDepartamentos(data);
            } else {
                console.error('Resposta inesperada ao carregar departamentos:', data);
                toastService.error('Formato de dados inesperado. Entre em contato com o suporte.');
                setDepartamentos([]);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
            setError("Não foi possível carregar os dados dos departamentos. Por favor, tente novamente.");
            toastService.error('Erro ao carregar departamentos. Tente novamente mais tarde.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (index: number) => {
        setDepartamentoSelecionado(departamentos[index]);
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleNovoDepartamento = () => {
        setDepartamentoSelecionado(criarDepartamentoVazio());
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleViewMore = (index: number) => {
        setDepartamentoSelecionado(departamentos[index]);
        setModoEdicao(false);
        setModalAberto(true);
    };

    const handleSaveDepartamento = async (data: Departamento) => {
        try {
            const isNovoDepartamento = !data.guid || data.guid.trim() === '';
            
            if (isNovoDepartamento) {
                await departamentoService.criar(data);
                toastService.success('Departamento criado com sucesso!');
            } else {
                await departamentoService.atualizar(data);
                toastService.success('Departamento atualizado com sucesso!');
            }
            
            setModalAberto(false);
            
            // Atualizar a lista
            const listaAtualizada = await departamentoService.listarTodos();
            setDepartamentos(listaAtualizada);
        } catch (error) {
            console.error('Erro ao salvar departamento:', error);
            toastService.error('Erro ao salvar departamento.');
        }
    };

    const handleDelete = async (index: number) => {
        const departamentoToDelete = departamentos[index];

        if (!departamentoToDelete?.guid) {
            toastService.error("Departamento não encontrado.");
            return;
        }

        try {
            await departamentoService.deletar(departamentoToDelete.guid);
            const novaLista = departamentos.filter((_, i) => i !== index);
            setDepartamentos(novaLista);
            toastService.success("Departamento excluído com sucesso!");
        } catch (error) {
            console.error("Erro ao excluir departamento:", error);
            toastService.error("Erro ao excluir departamento. Tente novamente.");
        }
    };

    // Show loading state when data is being fetched
    if (isLoading) {
        return <LoadingIndicator fullHeight message="Carregando departamentos..." />;
    }

    // Show error state only after loading is complete and there's an error
    if (error && !isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography color="error">{error}</Typography>
                <PrimaryButton onClick={() => window.location.reload()}>
                    Tentar Novamente
                </PrimaryButton>
            </Box>
        );
    }

    return (
        <Box>
            <InfoHeader
                title="Departamentos"
                message="Cadastre os departamentos existentes na sua empresa. Este cadastro será utilizado no controle e gestão dos seus planos de ação."
                isImportant={true}
                url="https://youtu.be/q8orQeP9zAQ"
                modalTitle="Entenda a aba Diretrizes"
            />

            <Paper elevation={3} sx={{ mt: 2 }}>
                <TableContainer sx={{ borderRadius: '8px' }}>
                    <Table aria-label="departamentos table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell><strong>Nome</strong></StyledTableCell>
                                <StyledTableCell><strong>Descrição</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {departamentos.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={3} align="center">
                                        <Typography sx={{ color: 'text.secondary', py: 2 }}>
                                            Nenhum departamento cadastrado
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                departamentos.map((departamento, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell 
                                            component="th" 
                                            scope="row"
                                            onClick={() => handleViewMore(index)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: 16, "&:hover": { textDecoration: "underline", color: "primary.main" } }}>
                                                {departamento.nome}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 16,
                                                    color: departamento.descricao && departamento.descricao.trim() !== ''
                                                        ? 'text.primary'
                                                        : 'secondary.main'
                                                }}
                                            >
                                                {departamento.descricao || 'Sem descrição'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <BasicMenu
                                                onEdit={() => handleEdit(index)}
                                                onDelete={() => handleDelete(index)}
                                                onViewMore={() => handleViewMore(index)}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <PrimaryButton variant="contained" onClick={handleNovoDepartamento}>
                    Adicionar
                </PrimaryButton>
            </Box>

            <DepartamentoModal
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                departamento={departamentoSelecionado}
                modoEdicao={modoEdicao}
                setDepartamento={modoEdicao ? setDepartamentoSelecionado : undefined}
                onSave={modoEdicao ? handleSaveDepartamento : undefined}
                onEditClick={() => setModoEdicao(true)}
                isNovoDepartamento={!departamentoSelecionado?.guid || departamentoSelecionado.guid.trim() === ''}
            />
        </Box>
    );
};

export default Departamentos;